import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import userApi from '../../../services/userApi';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';
import { Roles } from '../../../constants';
import FormProvider from '../../../components/form/FormProvider';
import TextField from '../../../components/form/TextField';
import PhoneField from '../../../components/form/PhoneField';
import SelectField from '../../../components/form/SelectField';

const LENDER_ROLE_OPTIONS = [
  { value: Roles.LenderAdmin, label: 'Lender Admin' },
  { value: Roles.AccountExecutive, label: 'Account Executive' },
];

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().email('Valid email is required'),
  phone: z.string().optional().default(''),
  role: z.string().min(1, 'Role is required'),
});

const InviteLenderAdminModal = ({ onClose }) => {
  const [inviteLenderAdmin, { isLoading }] = userApi.endpoints.inviteLenderAdmin.useMutation();
  const [error, setError] = useState('');

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: { firstName: '', lastName: '', emailAddress: '', phone: '', role: Roles.LenderAdmin },
  });

  const handleSubmit = async (data) => {
    setError('');
    try {
      await inviteLenderAdmin(data).unwrap();
      onClose();
    } catch (err) {
      setError(getAxiosErrorMessage(err, 'Failed to send invitation.'));
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}>
              <TextField name="firstName" label="First Name" required size="small" placeholder="First name" />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField name="lastName" label="Last Name" required size="small" placeholder="Last name" />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}>
              <TextField name="emailAddress" label="Email" type="email" required size="small" placeholder="email@example.com" />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <PhoneField name="phone" label="Phone" size="small" />
            </Grid>
          </Grid>
          <SelectField name="role" label="Role" options={LENDER_ROLE_OPTIONS} required size="small" sx={{ mb: 2 }} />
          <Alert severity="info">
            The user will receive an email with a link to set their password and activate their account.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isLoading} startIcon={<SendIcon />}>
            {isLoading ? 'Sending…' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default InviteLenderAdminModal;
